import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { scopedStorage } from '@lark-apaas/client-toolkit-lite';
import userStorage from '@/lib/user-storage';
import { formatLocalDate } from '@/lib/utils';
import type { IDailyHealthRecord } from './useAppState';
import { useTrainingDate } from './useTrainingSchedule';

const DAILY_HEALTH_KEY = 'daily_health_records';

interface DailyHealthContextValue {
  records: IDailyHealthRecord[];
  latestRecord: IDailyHealthRecord | null;
  todayRecord: IDailyHealthRecord | null;
  updateToday: (data: Partial<Pick<IDailyHealthRecord, 'restingHeartRate' | 'sleepHours' | 'weight' | 'mood'>>) => void;
  healthStreak: number;
  healthFullDays: number;
  getRecordByDate: (dateStr: string) => IDailyHealthRecord | null;
  isFullCheckIn: (rec: IDailyHealthRecord | null) => boolean;
  getCheckInCount: (rec: IDailyHealthRecord | null) => number;
  /** 最近 7 天睡眠平均（小时），有记录才算入 */
  avgSleepHours7d: number | null;
  /** 本周（周一到今天）好心情天数（mood === 'great' 的天数） */
  greatMoodDaysThisWeek: number;
}

const DailyHealthContext = createContext<DailyHealthContextValue | null>(null);

/** 顶层 Provider：在 App 根部挂载一次，保证全应用单例 */
export function DailyHealthProvider({ children }: { children: ReactNode }) {
  const [records, setRecords] = useState<IDailyHealthRecord[]>(() => {
    try {
      const raw = userStorage.getItem(DAILY_HEALTH_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    userStorage.setItem(DAILY_HEALTH_KEY, JSON.stringify(records));
  }, [records]);

  const today = useTrainingDate();

  const latestRecord = useMemo(() => {
    if (records.length === 0) return null;
    const sorted = [...records].sort((a, b) => b.date.localeCompare(a.date));
    return sorted[0];
  }, [records]);

  const todayRecord = useMemo(
    () => records.find(r => r.date === today) || null,
    [records, today],
  );

  const getRecordByDate = useCallback(
    (dateStr: string): IDailyHealthRecord | null => {
      return records.find(r => r.date === dateStr) || null;
    },
    [records],
  );

  const isFullCheckIn = useCallback((rec: IDailyHealthRecord | null) => {
    if (!rec) return false;
    return (
      rec.restingHeartRate !== undefined &&
      rec.sleepHours !== undefined &&
      rec.weight !== undefined &&
      rec.mood !== undefined
    );
  }, []);

  const getCheckInCount = useCallback((rec: IDailyHealthRecord | null) => {
    if (!rec) return 0;
    let count = 0;
    if (rec.restingHeartRate !== undefined) count++;
    if (rec.sleepHours !== undefined) count++;
    if (rec.weight !== undefined) count++;
    if (rec.mood !== undefined) count++;
    return count;
  }, []);

  const healthFullDays = useMemo(() => {
    return records.filter(r => isFullCheckIn(r)).length;
  }, [records, isFullCheckIn]);

  const healthStreak = useMemo(() => {
    if (records.length === 0) return 0;
    const dates = new Set(
      records
        .filter(
          r =>
            r.restingHeartRate !== undefined ||
            r.sleepHours !== undefined ||
            r.weight !== undefined ||
            r.mood !== undefined,
        )
        .map(r => r.date),
    );
    let count = 0;
    const d = new Date();
    // 先找到最近一个有打卡的日子（可以是今天，也可以是过去某天）
    let checked = 0;
    while (checked < 365) {
      const dateStr = formatLocalDate(d);
      if (dates.has(dateStr)) break;
      d.setDate(d.getDate() - 1);
      checked++;
    }
    if (checked >= 365) return 0; // 一年没打卡，直接 0
    // 从最近打卡日往回连续计数
    while (true) {
      const dateStr = formatLocalDate(d);
      if (dates.has(dateStr)) {
        count++;
        d.setDate(d.getDate() - 1);
      } else {
        break;
      }
    }
    return count;
  }, [records]);

  const updateToday = useCallback(
    (
      data: Partial<
        Pick<IDailyHealthRecord, 'restingHeartRate' | 'sleepHours' | 'weight' | 'mood'>
      >,
    ) => {
      setRecords(prev => {
        const writeDate = formatLocalDate();
        const existing = prev.findIndex(r => r.date === writeDate);
        if (existing >= 0) {
          const copy = [...prev];
          copy[existing] = { ...copy[existing], ...data };
          return copy;
        }
        return [...prev, { date: writeDate, ...data }].sort((a, b) =>
          a.date.localeCompare(b.date),
        );
      });
    },
    [today],
  );

  const avgSleepHours7d = useMemo(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    const startStr = formatLocalDate(sevenDaysAgo);
    const recent = records.filter(r => r.date >= startStr && r.sleepHours !== undefined);
    if (recent.length === 0) return null;
    const sum = recent.reduce((s, r) => s + (r.sleepHours || 0), 0);
    return Math.round((sum / recent.length) * 10) / 10;
  }, [records]);

  const greatMoodDaysThisWeek = useMemo(() => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0=周日, 1=周一
    // 本周一的日期
    const monday = new Date(now);
    monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    monday.setHours(0, 0, 0, 0);
    const mondayStr = formatLocalDate(monday);
    const todayStr = formatLocalDate(now);
    const weekRecords = records.filter(r => r.date >= mondayStr && r.date <= todayStr && r.mood === 'great');
    return weekRecords.length;
  }, [records]);

  const value: DailyHealthContextValue = {
    records,
    latestRecord,
    todayRecord,
    updateToday,
    healthStreak,
    healthFullDays,
    getRecordByDate,
    isFullCheckIn,
    getCheckInCount,
    avgSleepHours7d,
    greatMoodDaysThisWeek,
  };

  return (
    <DailyHealthContext.Provider value={value}>
      {children}
    </DailyHealthContext.Provider>
  );
}

export function useDailyHealth() {
  const ctx = useContext(DailyHealthContext);
  if (!ctx) {
    throw new Error('useDailyHealth must be used within <DailyHealthProvider>');
  }
  return ctx;
}
